import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Supported languages
export type Language = 'es' | 'en';

// Translation keys type
type TranslationKey = keyof typeof translations.es;

// Translations object
export const translations = {
  es: {
    // Navigation
    'nav.overview': 'Overview',
    'nav.partners': 'Partners',
    'nav.analytics': 'Analytics',
    'nav.settings': 'Settings',
    'nav.dashboard': 'Dashboard',
    'nav.campaigns': 'Campaigns',
    'nav.import': 'Importar',
    'nav.products': 'Productos',
    'nav.payments': 'Pagos',
    'nav.config': 'Config',
    'nav.home': 'Inicio',
    'nav.globalView': 'Vista Global',
    'nav.addClient': 'Agregar cliente',

    // Partner Detail
    'partner.summary': 'Resumen',
    'partner.orders': 'Orders',
    'partner.documents': 'Documentos',
    'partner.assets': 'Assets',
    'partner.activity': 'Actividad',
    'partner.budget': 'Budget',
    'partner.commissions': 'Comisiones',
    'partner.editCommissions': 'Editar Comisiones',
    'partner.save': 'Guardar',
    'partner.cancel': 'Cancelar',
    'partner.saving': 'Guardando...',
    'partner.current': 'Actual',

    // Charts and Data
    'chart.productsPerDay': 'Products per Day',
    'chart.ordersPerDay': 'Orders per Day',
    'chart.bySegment': 'Por Segmento',
    'chart.mediaSources': 'Media Sources',
    'chart.topProducts': 'Top Productos',
    'chart.events': 'Eventos',
    'chart.total': 'total',
    'chart.sales': 'ventas',
    'chart.viewAll': 'Ver todos',
    'chart.allMediaSources': 'Todos los Media Sources',
    'chart.allProducts': 'Todos los Productos',
    'chart.allEvents': 'Todos los Eventos',
    'chart.units': 'uds',

    // Orders Table
    'orders.monthlyOrders': 'Monthly Orders',
    'orders.searchPlaceholder': 'Buscar order ID o producto...',
    'orders.allMediaSources': 'Todos los media sources',
    'orders.orderId': 'Order ID',
    'orders.mediaSource': 'Media Source',
    'orders.products': 'Productos',
    'orders.productList': 'Lista Productos',
    'orders.commission': 'Commission',
    'orders.noMatchingOrders': 'No orders match the filters',
    'orders.showing': 'Mostrando',
    'orders.of': 'de',
    'orders.ordersLabel': 'orders',
    'orders.previous': 'Anterior',
    'orders.next': 'Siguiente',
    'orders.more': 'more',

    // Analytics
    'analytics.salesLast30': 'Sales (30 days)',
    'analytics.bestHour': 'Mejor hora',
    'analytics.bestDay': 'Best day',
    'analytics.dailyAvg': 'Promedio diario',
    'analytics.salesByHour': 'Ventas por Hora',
    'analytics.salesByDay': 'Sales by Day',
    'analytics.bestHours': 'Mejores Horas',
    'analytics.bestDays': 'Best Days',
    'analytics.salesByMediaSource': 'Ventas por Media Source',
    'analytics.insights': 'Insights',
    'analytics.noData': 'No hay datos suficientes',

    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.back': 'Volver',
    'common.active': 'Active',
    'common.paused': 'Paused',
    'common.status': 'Estado',
    'common.date': 'Fecha',
    'common.amount': 'Monto',
    'common.actions': 'Acciones',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.export': 'Exportar',
    'common.all': 'Todos',
    'common.none': 'Ninguno',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.confirm': 'Confirmar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Agregar',
    'common.close': 'Cerrar',
    'common.language': 'Idioma',

    // Time
    'time.today': 'Hoy',
    'time.yesterday': 'Ayer',
    'time.thisWeek': 'Esta semana',
    'time.thisMonth': 'Este mes',
    'time.lastMonth': 'Mes pasado',

    // Days
    'day.monday': 'Lunes',
    'day.tuesday': 'Martes',
    'day.wednesday': 'Wednesday',
    'day.thursday': 'Jueves',
    'day.friday': 'Viernes',
    'day.saturday': 'Saturday',
    'day.sunday': 'Domingo',

    // Settings
    'settings.general': 'General',
    'settings.users': 'Usuarios',
    'settings.clients': 'Clientes',
    'settings.webhooks': 'Webhooks',
    'settings.inviteUser': 'Invitar Usuario',
    'settings.pendingInvitations': 'Invitaciones Pendientes',

    // Status
    'status.active': 'Active',
    'status.paused': 'Paused',
    'status.prospect': 'Prospecto',
    'status.contacted': 'Contactado',
    'status.negotiating': 'Negociando',
    'status.churned': 'Churned',

    // Insights template
    'insights.bestHoursAre': 'Las mejores horas para este partner son entre',
    'insights.and': 'y',
    'insights.withMostActivity': 'with most activity on days',
    'insights.concentratePercent': 'Concentran el',
    'insights.ofTotalSales': 'de las ventas totales.',

    // Months
    'month.january': 'Enero',
    'month.february': 'Febrero',
    'month.march': 'Marzo',
    'month.april': 'Abril',
    'month.may': 'Mayo',
    'month.june': 'Junio',
    'month.july': 'Julio',
    'month.august': 'Agosto',
    'month.september': 'Septiembre',
    'month.october': 'Octubre',
    'month.november': 'Noviembre',
    'month.december': 'Diciembre',

    // Tabs
    'tab.summary': 'Resumen',
    'tab.analytics': 'Analytics',
    'tab.orders': 'Orders',
    'tab.products': 'Productos',
    'tab.payments': 'Pagos',
    'tab.budget': 'Budget',
    'tab.documents': 'Documentos',
    'tab.assets': 'Assets',
    'tab.activity': 'Actividad',
    'tab.config': 'Config',
    'tab.clicks': 'Clicks',
    'tab.mediaSources': 'Media Sources',
    'tab.insights': 'Insights',
    'tab.settings': 'Settings',

    // Portal tooltips
    'tooltip.commission': 'Your total commission earned this month based on attributed sales.',
    'tooltip.sales': 'Total units sold through your campaigns this month.',
    'tooltip.orders': 'Unique orders processed this month.',
    'tooltip.budget': 'Budget progress by segment. When it reaches 100%, the segment pauses.',
    'tooltip.todaySales': 'Ventas registradas hoy hasta el momento.',

    // Portal welcome / onboarding
    'welcome.title': '¡Bienvenido a tu Portal!',
    'welcome.step1.title': 'Tu Dashboard',
    'welcome.step1.desc': 'Ve tu rendimiento en tiempo real: ventas, comisiones y tendencias.',
    'welcome.step2.title': 'Tracking & Assets',
    'welcome.step2.desc': 'Use your tracking IDs to attribute sales and download campaign materials.',
    'welcome.step3.title': 'Comisiones',
    'welcome.step3.desc': 'Consulta el desglose de comisiones por segmento y el estado de tus pagos.',
    'welcome.step4.title': '¡Comencemos!',
    'welcome.step4.desc': 'Explora tu dashboard y revisa tu rendimiento del mes.',
    'welcome.skip': 'Saltar',
    'welcome.next': 'Siguiente',
    'welcome.done': 'Comenzar',

    // Empty states
    'empty.noOrders': 'No orders this month',
    'empty.noOrdersDesc': 'Orders will appear here when sales are registered.',
    'empty.noAssets': 'Sin assets disponibles',
    'empty.noAssetsDesc': 'Your team will upload campaign materials here.',
    'empty.noPayments': 'Sin pagos registrados',
    'empty.noPaymentsDesc': 'Payments will appear here when closings are generated.',
    'empty.noAnalytics': 'Sin datos de analytics',
    'empty.noAnalyticsDesc': 'Analytics will be available when you have sales.',

    // Date range
    'dateRange.thisMonth': 'Este mes',
    'dateRange.last7Days': 'Last 7 days',
    'dateRange.last30Days': 'Last 30 days',
    'dateRange.thisQuarter': 'Este trimestre',
    'dateRange.custom': 'Personalizado',

    // Quick actions
    'quickAction.copyTrackingId': 'Copiar Tracking ID',
    'quickAction.downloadReport': 'Descargar Reporte',
    'quickAction.contactSupport': 'Contactar Soporte',

    // Notifications
    'notifications.title': 'Notificaciones',
    'notifications.markAllRead': 'Mark all as read',
    'notifications.empty': 'No hay notificaciones',
    'notifications.all': 'Todas',
    'notifications.payments': 'Pagos',
    'notifications.budget': 'Budget',
    'notifications.assets': 'Assets',
    'notifications.system': 'Sistema',
    'notifications.today': 'Hoy',
    'notifications.yesterday': 'Ayer',
    'notifications.earlier': 'Anteriores',

    // Celebrations
    'celebration.firstSale': '¡Felicidades por tu primera venta!',
    'celebration.100sales': 'Amazing! You have reached 100 sales this month.',
    'celebration.budgetHalf': 'You are already over 50% of your budget.',

    // Export
    'export.dashboard': 'Exportar Dashboard',
    'export.generating': 'Generando reporte...',
  },
  en: {
    // Navigation
    'nav.overview': 'Overview',
    'nav.partners': 'Partners',
    'nav.analytics': 'Analytics',
    'nav.settings': 'Settings',
    'nav.dashboard': 'Dashboard',
    'nav.campaigns': 'Campaigns',
    'nav.import': 'Import',
    'nav.products': 'Products',
    'nav.payments': 'Payments',
    'nav.config': 'Config',
    'nav.home': 'Home',
    'nav.globalView': 'Global View',
    'nav.addClient': 'Add client',

    // Partner Detail
    'partner.summary': 'Summary',
    'partner.orders': 'Orders',
    'partner.documents': 'Documents',
    'partner.assets': 'Assets',
    'partner.activity': 'Activity',
    'partner.budget': 'Budget',
    'partner.commissions': 'Commissions',
    'partner.editCommissions': 'Edit Commissions',
    'partner.save': 'Save',
    'partner.cancel': 'Cancel',
    'partner.saving': 'Saving...',
    'partner.current': 'Current',

    // Charts and Data
    'chart.productsPerDay': 'Products per Day',
    'chart.ordersPerDay': 'Orders per Day',
    'chart.bySegment': 'By Segment',
    'chart.mediaSources': 'Media Sources',
    'chart.topProducts': 'Top Products',
    'chart.events': 'Events',
    'chart.total': 'total',
    'chart.sales': 'sales',
    'chart.viewAll': 'View all',
    'chart.allMediaSources': 'All Media Sources',
    'chart.allProducts': 'All Products',
    'chart.allEvents': 'All Events',
    'chart.units': 'units',

    // Orders Table
    'orders.monthlyOrders': 'Monthly Orders',
    'orders.searchPlaceholder': 'Search order ID or product...',
    'orders.allMediaSources': 'All media sources',
    'orders.orderId': 'Order ID',
    'orders.mediaSource': 'Media Source',
    'orders.products': 'Products',
    'orders.productList': 'Product List',
    'orders.commission': 'Commission',
    'orders.noMatchingOrders': 'No orders match the filters',
    'orders.showing': 'Showing',
    'orders.of': 'of',
    'orders.ordersLabel': 'orders',
    'orders.previous': 'Previous',
    'orders.next': 'Next',
    'orders.more': 'more',

    // Analytics
    'analytics.salesLast30': 'Sales (30 days)',
    'analytics.bestHour': 'Best hour',
    'analytics.bestDay': 'Best day',
    'analytics.dailyAvg': 'Daily average',
    'analytics.salesByHour': 'Sales by Hour',
    'analytics.salesByDay': 'Sales by Day',
    'analytics.bestHours': 'Best Hours',
    'analytics.bestDays': 'Best Days',
    'analytics.salesByMediaSource': 'Sales by Media Source',
    'analytics.insights': 'Insights',
    'analytics.noData': 'Not enough data',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.back': 'Back',
    'common.active': 'Active',
    'common.paused': 'Paused',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.amount': 'Amount',
    'common.actions': 'Actions',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.all': 'All',
    'common.none': 'None',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.confirm': 'Confirm',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.language': 'Language',

    // Time
    'time.today': 'Today',
    'time.yesterday': 'Yesterday',
    'time.thisWeek': 'This week',
    'time.thisMonth': 'This month',
    'time.lastMonth': 'Last month',

    // Days
    'day.monday': 'Monday',
    'day.tuesday': 'Tuesday',
    'day.wednesday': 'Wednesday',
    'day.thursday': 'Thursday',
    'day.friday': 'Friday',
    'day.saturday': 'Saturday',
    'day.sunday': 'Sunday',

    // Settings
    'settings.general': 'General',
    'settings.users': 'Users',
    'settings.clients': 'Clients',
    'settings.webhooks': 'Webhooks',
    'settings.inviteUser': 'Invite User',
    'settings.pendingInvitations': 'Pending Invitations',

    // Status
    'status.active': 'Active',
    'status.paused': 'Paused',
    'status.prospect': 'Prospect',
    'status.contacted': 'Contacted',
    'status.negotiating': 'Negotiating',
    'status.churned': 'Churned',

    // Insights template
    'insights.bestHoursAre': 'The best hours for this partner are between',
    'insights.and': 'and',
    'insights.withMostActivity': 'with most activity on',
    'insights.concentratePercent': 'They concentrate',
    'insights.ofTotalSales': 'of total sales.',

    // Months
    'month.january': 'January',
    'month.february': 'February',
    'month.march': 'March',
    'month.april': 'April',
    'month.may': 'May',
    'month.june': 'June',
    'month.july': 'July',
    'month.august': 'August',
    'month.september': 'September',
    'month.october': 'October',
    'month.november': 'November',
    'month.december': 'December',

    // Tabs
    'tab.summary': 'Summary',
    'tab.analytics': 'Analytics',
    'tab.orders': 'Orders',
    'tab.products': 'Products',
    'tab.payments': 'Payments',
    'tab.budget': 'Budget',
    'tab.documents': 'Documents',
    'tab.assets': 'Assets',
    'tab.activity': 'Activity',
    'tab.config': 'Config',
    'tab.clicks': 'Clicks',
    'tab.mediaSources': 'Media Sources',
    'tab.insights': 'Insights',
    'tab.settings': 'Settings',

    // Portal tooltips
    'tooltip.commission': 'Your total commission earned this month based on attributed sales.',
    'tooltip.sales': 'Total units sold through your campaigns this month.',
    'tooltip.orders': 'Unique orders processed this month.',
    'tooltip.budget': 'Budget progress by segment. When it reaches 100%, the segment is paused.',
    'tooltip.todaySales': 'Sales recorded today so far.',

    // Portal welcome / onboarding
    'welcome.title': 'Welcome to your Portal!',
    'welcome.step1.title': 'Your Dashboard',
    'welcome.step1.desc': 'See your real-time performance: sales, commissions, and trends.',
    'welcome.step2.title': 'Tracking & Assets',
    'welcome.step2.desc': 'Use your tracking IDs to attribute sales and download campaign materials.',
    'welcome.step3.title': 'Commissions',
    'welcome.step3.desc': 'Check commission breakdown by segment and your payment status.',
    'welcome.step4.title': 'Let\'s Go!',
    'welcome.step4.desc': 'Explore your dashboard and review your monthly performance.',
    'welcome.skip': 'Skip',
    'welcome.next': 'Next',
    'welcome.done': 'Get Started',

    // Empty states
    'empty.noOrders': 'No orders this month',
    'empty.noOrdersDesc': 'Orders will appear here when sales are recorded.',
    'empty.noAssets': 'No assets available',
    'empty.noAssetsDesc': 'Your team will upload campaign materials here.',
    'empty.noPayments': 'No payments recorded',
    'empty.noPaymentsDesc': 'Payments will appear here when settlements are generated.',
    'empty.noAnalytics': 'No analytics data',
    'empty.noAnalyticsDesc': 'Analytics will be available once you have sales.',

    // Date range
    'dateRange.thisMonth': 'This Month',
    'dateRange.last7Days': 'Last 7 Days',
    'dateRange.last30Days': 'Last 30 Days',
    'dateRange.thisQuarter': 'This Quarter',
    'dateRange.custom': 'Custom',

    // Quick actions
    'quickAction.copyTrackingId': 'Copy Tracking ID',
    'quickAction.downloadReport': 'Download Report',
    'quickAction.contactSupport': 'Contact Support',

    // Notifications
    'notifications.title': 'Notifications',
    'notifications.markAllRead': 'Mark all as read',
    'notifications.empty': 'No notifications',
    'notifications.all': 'All',
    'notifications.payments': 'Payments',
    'notifications.budget': 'Budget',
    'notifications.assets': 'Assets',
    'notifications.system': 'System',
    'notifications.today': 'Today',
    'notifications.yesterday': 'Yesterday',
    'notifications.earlier': 'Earlier',

    // Celebrations
    'celebration.firstSale': 'Congratulations on your first sale!',
    'celebration.100sales': 'Amazing! You\'ve reached 100 sales this month.',
    'celebration.budgetHalf': 'You\'ve passed 50% of your budget.',

    // Export
    'export.dashboard': 'Export Dashboard',
    'export.generating': 'Generating report...',
  },
} as const;

// Language context
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'es',
  setLanguage: () => {},
  t: (key) => key,
});

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('relo-language');
    if (saved === 'en' || saved === 'es') return saved;
    // Check browser language
    const browserLang = navigator.language.slice(0, 2);
    return browserLang === 'en' ? 'en' : 'es';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('relo-language', lang);
  };

  // Translation function
  const t = (key: TranslationKey): string => {
    return translations[language][key] || key;
  };

  useEffect(() => {
    // Update HTML lang attribute
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook for using translations
export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}

// Language names for display
export const languageNames: Record<Language, string> = {
  es: 'Spanish',
  en: 'English',
};

// Language flags/codes for display
export const languageCodes: Record<Language, string> = {
  es: 'ES',
  en: 'EN',
};

// Month keys for easy lookup
const monthKeys = [
  'month.january', 'month.february', 'month.march', 'month.april',
  'month.may', 'month.june', 'month.july', 'month.august',
  'month.september', 'month.october', 'month.november', 'month.december'
] as const;

// Helper to get month name by index (1-12)
export function getMonthName(monthIndex: number, language: Language): string {
  const key = monthKeys[monthIndex - 1];
  return translations[language][key] || '';
}
